import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { ReactElement } from "react";
import { useForm } from "react-hook-form";
import z from "zod";

import {
  CreateDoNotContactRequestMutation,
  CreateDoNotContactRequestMutationVariables,
  DoNotContactRequestAction,
  DoNotContactRequestSourceType,
} from "~frontend/__generated__/graphql";
import { RhfTextField } from "~frontend/components/forms/react-hook-form-components";
import {
  CREATE_DO_NOT_CONTACT_REQUEST,
  LIST_DO_NOT_CONTACT_REQUESTS,
} from "~frontend/graphql/doNotContactRequests";
import useMutation from "~frontend/util/useMutation";

export const RemoveContactFormSchema = z.object({
  notes: z.string().optional(),
});

export type RemoveContactFormValues = z.infer<typeof RemoveContactFormSchema>;

export function RemoveContactModal({
  open,
  onClose,
  parentOnClose,
  phoneNumber,
}: {
  open: boolean;
  onClose: () => void;
  parentOnClose?: () => void;
  phoneNumber: string;
}): ReactElement {
  const methods = useForm<RemoveContactFormValues>({
    resolver: zodResolver(RemoveContactFormSchema),
    defaultValues: {
      notes: "",
    },
  });

  const { control, handleSubmit, reset } = methods;

  const [createDoNotContactRequest] = useMutation<
    CreateDoNotContactRequestMutation,
    CreateDoNotContactRequestMutationVariables
  >(CREATE_DO_NOT_CONTACT_REQUEST, {
    onErrorSnackbarMessage: "Error removing contact from list",
    refetchQueries: [LIST_DO_NOT_CONTACT_REQUESTS],
    awaitRefetchQueries: true,
  });

  const onSubmit = async (input: RemoveContactFormValues) => {
    await createDoNotContactRequest({
      variables: {
        input: {
          ...input,
          phoneNumber: phoneNumber,
          action: DoNotContactRequestAction.Remove,
          sourceType: DoNotContactRequestSourceType.StaffManual,
        },
      },
    });
    resetAndClose();
    if (parentOnClose) {
      parentOnClose();
    }
  };

  const resetAndClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open && !!phoneNumber} onClose={resetAndClose} maxWidth="sm">
      <DialogTitle>
        {`Remove `}
        <Box sx={{ fontWeight: 700 }} component={"span"}>
          {phoneNumber}
        </Box>
        {` from Do Not Contact List`}
        <Typography>
          {
            "Removing this contact from the Do Not Contact List will allow them to receive messages from Scale to Win Text, Scale to Win Dialer, and Spoke again."
          }
        </Typography>
      </DialogTitle>
      <DialogContent>
        <form>
          <RhfTextField
            control={control}
            name="notes"
            label="Notes"
            optional={true}
            bottomLabel="Please include the reason for removing from the Do Not Contact List and links to any relevant tickets or slack threads."
            fullWidth
            multiline
            rows={4}
          />
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={resetAndClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          onClick={handleSubmit(onSubmit)}
        >
          Remove contact from list
        </Button>
      </DialogActions>
    </Dialog>
  );
}
